import React, { useMemo } from 'react';
import Sparks from './Sparks';

/**
 * Immersive-watch special effect layers ("sanctuary" theme experiments).
 * Every layer is pure CSS animation (keyframes in App.css), absolutely
 * positioned, pointer-events: none, and sits between the cover image and
 * the prayer text. `speed` is the admin-managed multiplier shared with
 * the sparks setting.
 */
export const EFFECT_STYLES = ['sparks', 'trails', 'orbs', 'beams', 'ripples', 'candles', 'none'];
export const EFFECT_LABELS = {
  sparks: 'Sparks',
  trails: 'Spark Trails',
  orbs: 'Rising Orbs',
  beams: 'Light Beams',
  ripples: 'Ripples',
  candles: 'Candles',
  none: 'No Effect',
};

const clampSpeed = (speed) => Math.min(5, Math.max(0.1, Number(speed) || 1));

/* Soft glowing orbs drifting upward at varying speeds. */
const RisingOrbs = ({ speed = 1 }) => {
  const s = clampSpeed(speed);
  const orbs = useMemo(() => Array.from({ length: 12 }, () => ({
    left: Math.random() * 100,
    size: 22 + Math.random() * 46,
    duration: (16 + Math.random() * 14) / s,
    delay: -(Math.random() * 30) / s,
    drift: (Math.random() * 2 - 1) * 90,
    sway: 5 + Math.random() * 5,
    peak: 0.10 + Math.random() * 0.16,
    warm: Math.random() < 0.7,
  })), [s]);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true" data-testid="immersive-orbs-layer">
      {orbs.map((o, i) => (
        <span
          key={i}
          className="absolute bottom-0"
          style={{
            left: `${o.left}%`,
            opacity: 0,
            animation: `sparkRise ${o.duration}s linear ${o.delay}s infinite`,
            '--spark-peak': o.peak,
          }}
        >
          <span
            style={{
              display: 'block',
              width: `${o.size}px`,
              height: `${o.size}px`,
              borderRadius: '9999px',
              filter: 'blur(2px)',
              background: o.warm
                ? 'radial-gradient(circle, rgba(255,209,102,0.9) 0%, rgba(255,190,80,0.25) 45%, rgba(255,190,80,0) 70%)'
                : 'radial-gradient(circle, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.2) 45%, rgba(255,255,255,0) 70%)',
              animation: `sparkSway ${o.sway}s ease-in-out ${o.delay}s infinite alternate`,
              '--spark-drift': `${o.drift}px`,
            }}
          />
        </span>
      ))}
    </div>
  );
};

/* Subtle rotating beams of light pivoting from above the viewport. */
const LightBeams = ({ speed = 1 }) => {
  const s = clampSpeed(speed);
  const beams = useMemo(() => Array.from({ length: 5 }, (_, i) => ({
    left: 12 + i * 19 + (Math.random() * 6 - 3),
    width: 90 + Math.random() * 120,
    duration: (14 + Math.random() * 10) / s,
    delay: -(Math.random() * 20) / s,
    swing: 8 + Math.random() * 10,
    peak: 0.10 + Math.random() * 0.10,
    warm: Math.random() < 0.7,
  })), [s]);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true" data-testid="immersive-beams-layer" style={{ mixBlendMode: 'screen' }}>
      {beams.map((b, i) => (
        <span
          key={i}
          className="absolute"
          style={{
            left: `${b.left}%`,
            top: '-12vh',
            width: `${b.width}px`,
            height: '135vh',
            opacity: b.peak,
            transformOrigin: 'top center',
            filter: 'blur(14px)',
            background: b.warm
              ? 'linear-gradient(to bottom, rgba(255,209,102,0.55) 0%, rgba(255,190,80,0.12) 55%, rgba(255,190,80,0) 100%)'
              : 'linear-gradient(to bottom, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.1) 55%, rgba(255,255,255,0) 100%)',
            animation: `beamSwing ${b.duration}s ease-in-out ${b.delay}s infinite alternate`,
            '--beam-swing': `${b.swing}deg`,
          }}
        />
      ))}
    </div>
  );
};

/* Soft circular waves expanding and fading from scattered points. */
const Ripples = ({ speed = 1 }) => {
  const s = clampSpeed(speed);
  const ripples = useMemo(() => Array.from({ length: 7 }, () => ({
    left: 12 + Math.random() * 76,
    top: 18 + Math.random() * 64,
    size: 140 + Math.random() * 200,
    duration: (7 + Math.random() * 5) / s,
    delay: -(Math.random() * 12) / s,
    peak: 0.14 + Math.random() * 0.14,
    warm: Math.random() < 0.7,
  })), [s]);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true" data-testid="immersive-ripples-layer">
      {ripples.map((r, i) => (
        <span
          key={i}
          className="absolute rounded-full"
          style={{
            left: `${r.left}%`,
            top: `${r.top}%`,
            width: `${r.size}px`,
            height: `${r.size}px`,
            marginLeft: `-${r.size / 2}px`,
            marginTop: `-${r.size / 2}px`,
            border: r.warm ? '1.5px solid rgba(255,209,102,0.8)' : '1.5px solid rgba(255,255,255,0.7)',
            boxShadow: r.warm ? '0 0 18px 2px rgba(255,190,80,0.25) inset' : '0 0 18px 2px rgba(255,255,255,0.2) inset',
            opacity: 0,
            animation: `rippleExpand ${r.duration}s ease-out ${r.delay}s infinite`,
            '--ripple-peak': r.peak,
          }}
        />
      ))}
    </div>
  );
};

/* Lit candles that appear to glow from BEHIND the cover image: the whole
   layer uses mix-blend screen + blur so only the warm light bleeds
   through, like flames somewhere behind the scene. Each candle = a
   breathing halo + a tiny flickering flame core (reuses the app's
   flame-flicker keyframes). */
const Candles = ({ speed = 1 }) => {
  const s = clampSpeed(speed);
  const candles = useMemo(() => Array.from({ length: 9 }, () => ({
    left: 4 + Math.random() * 92,
    bottom: 2 + Math.random() * 13,
    halo: 60 + Math.random() * 90,
    flameH: 11 + Math.random() * 9,
    breathe: (3.2 + Math.random() * 2.6) / s,
    flick: (1.3 + Math.random() * 1.1) / s,
    delay: -(Math.random() * 6) / s,
    peak: 0.18 + Math.random() * 0.2,
    depth: Math.random(),
  })), [s]);
  return (
    <div
      className="absolute inset-0 overflow-hidden pointer-events-none"
      aria-hidden="true"
      data-testid="immersive-candles-layer"
      style={{ mixBlendMode: 'screen' }}
    >
      {candles.map((c, i) => (
        <span
          key={i}
          className="absolute"
          style={{ left: `${c.left}%`, bottom: `${c.bottom}%`, filter: `blur(${2 + c.depth * 4}px)` }}
        >
          {/* breathing warm halo */}
          <span
            style={{
              position: 'absolute',
              left: '50%',
              bottom: '-10px',
              width: `${c.halo}px`,
              height: `${c.halo}px`,
              marginLeft: `-${c.halo / 2}px`,
              borderRadius: '9999px',
              background: 'radial-gradient(circle, rgba(255,176,74,0.85) 0%, rgba(249,115,22,0.28) 40%, rgba(249,115,22,0) 70%)',
              animation: `candleHaloBreathe ${c.breathe}s ease-in-out ${c.delay}s infinite`,
              '--halo-peak': c.peak,
            }}
          />
          {/* flickering flame core */}
          <span
            className="flame"
            style={{
              position: 'relative',
              display: 'block',
              width: `${c.flameH * 0.45}px`,
              height: `${c.flameH}px`,
              borderRadius: '50% 50% 50% 50% / 62% 62% 38% 38%',
              background: 'linear-gradient(to top, rgba(255,140,40,0.95) 0%, rgba(255,209,102,0.95) 55%, rgba(255,244,214,0.95) 100%)',
              color: 'rgba(255,190,80,0.9)',
              opacity: 0.5 + c.peak,
              animationDuration: `${c.flick}s`,
              animationDelay: `${c.delay}s`,
            }}
          />
        </span>
      ))}
    </div>
  );
};

/* Sparks with a streaming comet tail as they float up. Same motion as
   the classic sparks (sparkRise + sparkSway) — each head just trails a
   thin fading streak of light beneath it. */
const SparkTrails = ({ speed = 1 }) => {
  const s = clampSpeed(speed);
  const sparks = useMemo(() => Array.from({ length: 22 }, () => ({
    left: Math.random() * 100,
    size: 1.8 + Math.random() * 2.4,
    tail: 34 + Math.random() * 60,
    duration: (9 + Math.random() * 10) / s,
    delay: -(Math.random() * 19) / s,
    drift: (Math.random() * 2 - 1) * 60,
    sway: 3 + Math.random() * 4,
    peak: 0.3 + Math.random() * 0.45,
    warm: Math.random() < 0.65,
  })), [s]);
  return (
    <div
      className="absolute inset-0 overflow-hidden pointer-events-none"
      aria-hidden="true"
      data-testid="immersive-trails-layer"
    >
      {sparks.map((t, i) => {
        const headColor = t.warm ? 'rgba(255, 209, 102, 0.9)' : 'rgba(255, 255, 255, 0.85)';
        const tailColor = t.warm ? 'rgba(255, 190, 80,' : 'rgba(255, 255, 255,';
        return (
          <span
            key={i}
            className="absolute bottom-0"
            style={{
              left: `${t.left}%`,
              opacity: 0,
              animation: `sparkRise ${t.duration}s linear ${t.delay}s infinite`,
              '--spark-peak': t.peak,
            }}
          >
            {/* sway wrapper carries head + tail together */}
            <span
              style={{
                display: 'block',
                position: 'relative',
                animation: `sparkSway ${t.sway}s ease-in-out ${t.delay}s infinite alternate`,
                '--spark-drift': `${t.drift}px`,
              }}
            >
              {/* streaming tail below the head */}
              <span
                style={{
                  position: 'absolute',
                  top: `${t.size - 1}px`,
                  left: '50%',
                  width: `${Math.max(1.5, t.size * 0.6)}px`,
                  marginLeft: `-${Math.max(1.5, t.size * 0.6) / 2}px`,
                  height: `${t.tail}px`,
                  borderRadius: '9999px',
                  filter: 'blur(0.5px)',
                  background: `linear-gradient(to bottom, ${tailColor}0.55) 0%, ${tailColor}0.18) 55%, ${tailColor}0) 100%)`,
                }}
              />
              {/* head */}
              <span
                style={{
                  display: 'block',
                  width: `${t.size}px`,
                  height: `${t.size}px`,
                  borderRadius: '9999px',
                  background: headColor,
                  boxShadow: t.warm
                    ? '0 0 6px 2px rgba(255, 190, 80, 0.45)'
                    : '0 0 6px 2px rgba(255, 255, 255, 0.35)',
                }}
              />
            </span>
          </span>
        );
      })}
    </div>
  );
};

const ImmersiveEffect = ({ style = 'sparks', speed = 1 }) => {
  switch (style) {
    case 'trails': return <SparkTrails speed={speed} />;
    case 'orbs': return <RisingOrbs speed={speed} />;
    case 'beams': return <LightBeams speed={speed} />;
    case 'ripples': return <Ripples speed={speed} />;
    case 'candles': return <Candles speed={speed} />;
    case 'none': return null;
    default: return <Sparks speed={speed} />;
  }
};

export default ImmersiveEffect;
